import React from "react"
import { browserHistory } from "react-router"
import { Paper, CircularProgress, FloatingActionButton, SelectField, MenuItem } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import autobind from "class-autobind"
import strings from "../../localization/strings"
import { IClientDocument, IClientSearch, EClientActiveStatus } from "../../models"
import * as api from "../../api"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import HierarchySettingsStore from "../../stores/HierarchySettingsStore"
import AuthenticationStore from "../../stores/AuthenticationStore"
import TableActionBar from "../../components/layout/TableActionBar"
import * as reportAction from "../../actions/ReportAction"
import ClientsTable from "./ClientsTable"
import AddClientDialog from "./AddClientDialog"
import * as permissionHelper from "../../helpers/permissionHelper"
import { EnumValues } from "enum-values"
import { debounce } from "lodash"
interface IState {
    clients: IClientDocument[]
    selectedClientIds: string[]
    selectedActiveStatus: EClientActiveStatus | "all"
    searchText: string
    loading: boolean
    total: number
    offset: number
    limit: number
    openAddClientDialog: boolean
}

export default class Clients extends React.Component<{}, IState> {
    constructor(props) {
        super(props)
        this.state = {
            clients: [],
            selectedClientIds: [],
            selectedActiveStatus: EClientActiveStatus.Active,
            searchText: "",
            loading: false,
            total: 0,
            offset: 0,
            limit: 20,
            openAddClientDialog: false
        }
        autobind(this)
    }

    componentDidMount() {
        document.title = `${strings.Clients} | ${strings.Findd_Grandd_Central}`
        this.getClients()
        HierarchySettingsStore.on("global_change", this.handleChangeGlobalLevel)
    }

    componentWillUnmount() {
        HierarchySettingsStore.removeListener("global_change", this.handleChangeGlobalLevel)
    }

    reloadClientsTable() {
        this.getClients()
    }

    handleChangeGlobalLevel() {
        this.setState({ offset: 0, searchText: "" }, () => this.getClients())
    }

    getClients() {
        this.setState({ loading: true }, () => {
            api.getClients(AccountStore.getCurrentAccount().Id, HierarchySettingsStore.getGlobalLevelItemFilterId(), this.state.offset / this.state.limit, this.state.limit, this.state.searchText, this.state.selectedActiveStatus)
                .then(res => {
                    const data = res.data as IClientSearch
                    this.setState({
                        loading: false,
                        total: data.TotalDocumentCount,
                        clients: data.Documents,
                        selectedClientIds: []
                    })
                })
        })
    }

    getClientsDataDownload() {
        reportAction.downloadClientsData(AccountStore.getCurrentAccount().Id, HierarchySettingsStore.getGlobalLevelItemFilterId(), 0, this.state.total, this.state.searchText, this.state.selectedActiveStatus)
    }

    handleDeleteClient() {
        AppStateStore.setProgressSnackbarContent("loading")
        api.deleteClient({ data: this.state.selectedClientIds }).then(() => {
            AppStateStore.setProgressSnackbarContent(strings.Successfully_deleted)
            this.getClients()
        })
            .catch(err => {
                AppStateStore.setProgressSnackbarContent(err.data.message)
            })
    }

    onSearchChange = debounce(searchText => {
        this.setState({ searchText, offset: 0 }, () => this.getClients())
    }, 1000)

    render() {
        return (
            <div className="page-container">
                <div className="page-container-parent">
                    {permissionHelper.hasPermission(AuthenticationStore.Permissions.Clients.AddClients) &&
                        <FloatingActionButton className="fixed-add" style={{ top: 83, right: 40, zIndex: 998 }} secondary={true} onTouchTap={() => this.setState({ openAddClientDialog: true })}>
                            <ContentAdd />
                        </FloatingActionButton>
                    }
                    <Paper zDepth={1} className="page-paper">
                        <SelectField
                            floatingLabelText={strings.Active_Status}
                            value={this.state.selectedActiveStatus}
                            onChange={(event, key, selectedActiveStatus) =>
                                this.setState({ selectedActiveStatus }, () => this.getClients())
                            }
                            style={{ marginLeft: 35, marginTop: -15, marginBottom: -10, width: 250 }}
                        >
                            {EnumValues.getNamesAndValues(EClientActiveStatus).map((item, i) =>
                                <MenuItem
                                    key={i}
                                    value={item.value}
                                    primaryText={strings[item.name]}
                                />
                            )}
                            <MenuItem
                                value={"all"}
                                primaryText={strings.All}
                            />
                        </SelectField>
                        <TableActionBar
                            rowSelectionCount={this.state.selectedClientIds.length}
                            singleSelectedMessage={strings.Client_Selected}
                            multipleSelectedMessage={strings.x_Client_Selected}
                            title={strings.Clients}
                            onSearch={searchText => this.onSearchChange(searchText)}
                            reloadTable={this.reloadClientsTable}
                            exportTable={this.getClientsDataDownload}
                            searchText={this.state.searchText}
                            onDeleteClick={() => AppStateStore.setConfirmDialogContent({
                                content: strings.Confirm_delete_client,
                                confirm: () => this.handleDeleteClient()
                            })}
                            readonly={!permissionHelper.hasPermission(AuthenticationStore.Permissions.Clients.DeleteClients)}
                        />
                        <div style={{ padding: 20 }}>
                            {this.state.loading ?
                                <div className="spinner"><CircularProgress /></div> :
                                <div>
                                    {!this.state.clients.length ?
                                        <h2 style={{ textAlign: "center", marginTop: 25 }}>
                                            {strings.No_client_found}
                                        </h2> :
                                        <ClientsTable
                                            readonly={!permissionHelper.hasPermission(AuthenticationStore.Permissions.Clients.DeleteClients)}
                                            clients={this.state.clients}
                                            total={this.state.total}
                                            offset={this.state.offset}
                                            limit={this.state.limit}
                                            limitOptions={[20, 50, 100]}
                                            onPageClick={offset => this.setState({ offset }, () => this.getClients())}
                                            onLimitChange={limit => this.setState({ offset: 0, limit }, () => this.getClients())}
                                            handleSelect={selectedClientIds => this.setState({ selectedClientIds })}
                                            selectedClientIds={this.state.selectedClientIds}
                                            handleRowClick={clientId => browserHistory.replace(`/client-details/${clientId}`)}
                                        />
                                    }
                                </div>
                            }
                        </div>
                    </Paper>
                </div>
                {this.state.openAddClientDialog &&
                    <AddClientDialog
                        handleAdd={client => {
                            this.setState({ openAddClientDialog: false })
                            AppStateStore.setProgressSnackbarContent("loading")
                            api.addClient(client)
                                .then(() => {
                                    AppStateStore.triggerGetFreeAccountMetricsData()
                                    AppStateStore.setProgressSnackbarContent(strings.Successfully_added)
                                    this.getClients()
                                })
                                .catch(e =>
                                    (e.response.data.message) && (
                                        e.response.data.message === "Cannot add over 10 clients for free account." ?
                                            AppStateStore.setFreeAccountDataLimitUpgradeDialogContent(strings.Cannot_add_over_10_clients_for_free_account_Please_upgrade_your_account_plan_to_add_more) :
                                            AppStateStore.setProgressSnackbarContent(e.response.data.message)
                                    )
                                )
                        }}
                        handleClose={() => this.setState({ openAddClientDialog: false })}
                    />
                }
            </div>
        )
    }
}